import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { MapModuleLayer } from "@iventis/map-engine";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { findNode, NodeWithHierarchy, TreeBrowserStateInterface } from "@iventis/tree-browser";
import { SitemapVersionType } from "@iventis/domain-model/model/sitemapVersionType";

export const defaultGroupName = "New Group";

export const getParentNode = (tree: Node, node: Node) => {
    const parentNode = findNode([tree], node.parentId);

    if (parentNode == null) {
        return { parentNode, isParentGrouped: false };
    }

    return {
        parentNode,
        isParentGrouped: findNode([tree], parentNode.parentId).type === NodeType.Folder,
    };
};

export const getParentLayer = <TLayer extends MapLayer>(mapLayers: TLayer[], parentNode: Node) => {
    if (mapLayers != null && mapLayers.length !== 0 && parentNode != null) {
        const stateLayer: TLayer = mapLayers.find((mapLayer) => mapLayer.id === parentNode.id);
        if (stateLayer != null) {
            return stateLayer;
        }
    }
    return null;
};

export const getMapLayerNodeFromOriginId = (originLayerId: string, treeBrowserContext: TreeBrowserStateInterface<NodeWithHierarchy>) => {
    const layerId = findNode(findNode(treeBrowserContext.currentState.tree.childNodes, treeBrowserContext.currentState.mainNodeId).childNodes, originLayerId)?.id;
    return layerId;
};

export type ToggleableNodeAttributes = {
    [K in keyof MapModuleLayer]: MapModuleLayer[K] extends boolean ? K : never;
}[keyof MapModuleLayer];

/**
 *  Function to get the level name of a map object based on its sitemap and level index
 */
export const getMapObjectLevelName = (sitemaps: Sitemap[], mapObjectLevel: number, mapObjectSitemapId: string, sitemapsInView: MapSitemapConfig[]): string | undefined => {
    if (mapObjectLevel == null || mapObjectSitemapId == null) return undefined;

    const sitemap = sitemaps?.find((item) => item.id === mapObjectSitemapId);
    if (sitemap == null) return undefined;

    // Return undefined if no versions exist for the found sitemap
    if (sitemap.versions == null || sitemap.versions.length === 0) return undefined;
    // Check if the map object's sitemap is among the currently viewed sitemaps
    const sitemapInView = sitemapsInView?.find((item) => item.sitemapId === mapObjectSitemapId);

    let sitemapVersion2d = null;
    let sitemapVersion3d = null;

    if (sitemapInView != null) {
        // If a matching sitemap in view is found, use those versions to get the level name
        sitemapVersion2d = sitemap.versions.find((item) => item.id === sitemapInView.version2dId);
        sitemapVersion3d = sitemap.versions.find((item) => item.id === sitemapInView.version3dId);
    } else {
        // If no matching sitemap in view is found, fall back to the most recent versions
        const sitemapVersions2d = sitemap.versions.filter((version) => version.type === SitemapVersionType.TwoDimensional);
        if (sitemapVersions2d.length > 0) {
            sitemapVersion2d = sitemapVersions2d.reduce((latest, current) => (current.date > latest.date ? current : latest));
        }
        const sitemapVersions3d = sitemap.versions.filter((version) => version.type === SitemapVersionType.ThreeDimensional);
        if (sitemapVersions3d.length > 0) {
            sitemapVersion3d = sitemapVersions3d.reduce((latest, current) => (current.date > latest.date ? current : latest));
        }
    }

    const sitemapVersion2dLevel = sitemapVersion2d?.sitemapVersionLevels.find((item) => item.levelIndex === mapObjectLevel);
    const sitemapVersion3dLevel = sitemapVersion3d?.sitemapVersionLevels.find((item) => item.levelIndex === mapObjectLevel);

    // If both 2D and 3D levels exist and have different names, concatenate them with a slash, otherwise return the existing level name
    if (sitemapVersion2dLevel != null && sitemapVersion3dLevel != null && sitemapVersion2dLevel.levelName !== sitemapVersion3dLevel.levelName) {
        return `${sitemapVersion2dLevel.levelName} / ${sitemapVersion3dLevel.levelName}`;
    }
    if (sitemapVersion2dLevel) return sitemapVersion2dLevel.levelName;
    if (sitemapVersion3dLevel) return sitemapVersion3dLevel.levelName;
    return undefined;
};
